/**
 * Stripe.js loader.
 *
 * The publishable key comes from the *server* (`/api/payment/stripe/config`)
 * rather than the build. That way live/test can never drift apart: whichever
 * mode the server's secret key is in, the browser automatically matches it.
 * A mismatch would break every payment ("no such payment_intent"), and it is
 * exactly the kind of mistake that only shows up on go-live day.
 *
 * REACT_APP_STRIPE_PUBLISHABLE_KEY is only a fallback for when the API can't be
 * reached.
 */
import { loadStripe } from '@stripe/stripe-js';
import { apiUrl } from '../api';

let stripePromise;

/** Resolves to a Stripe instance (or null if no key is configured anywhere). */
export function getStripe() {
  if (!stripePromise) {
    stripePromise = (async () => {
      try {
        const res = await fetch(apiUrl('/api/payment/stripe/config'));
        const data = await res.json();
        if (data && data.publishableKey) return loadStripe(data.publishableKey);
      } catch {
        // Server unreachable - fall through to the build-time key.
      }

      const fallback = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!fallback) {
        console.warn(
          '⚠️  No Stripe publishable key: the server did not return one and ' +
            'REACT_APP_STRIPE_PUBLISHABLE_KEY is not set.'
        );
        return null;
      }
      return loadStripe(fallback);
    })();
  }
  return stripePromise;
}

/** Brand-matched look for Stripe Elements. */
export const stripeAppearance = {
  theme: 'flat',
  variables: {
    colorPrimary: '#4B0213',
    colorBackground: '#FFFEF2',
    colorText: '#391C14',
    colorDanger: '#c0392b',
    fontFamily: '"Poppins", sans-serif',
    borderRadius: '10px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': { border: '1px solid rgba(75, 2, 19, 0.25)', boxShadow: 'none' },
    '.Input:focus': { border: '1px solid #4B0213', boxShadow: '0 0 0 2px rgba(75, 2, 19, 0.12)' },
    '.Label': { fontWeight: '600', color: '#391C14' },
  },
};
